<template>
  <div class="root">
    <div class="row">
      <div class="col">
        <label
          v-if="name"
          :for="id? name+id:name"
          class="mb-0">
          {{ name }}
          <coozzy-info-circle-icon
            v-if="tooltipText"
            v-b-tooltip.hover.html="tooltipText" />
        </label>
        <div
          v-if="secondaryTitle"
          class="secondary-title ml-2">
          {{ secondaryTitle }}
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <span
          v-if="limit !== -1 && !isReadOnly"
          class="limit text-grey-light">
          {{ valueInputLength }}/{{ limit }}
        </span>
        <b-form-input
          v-if="!isReadOnly"
          :id="id? name+id:name"
          ref="bFormInput"
          :value="valueInput"
          :step="step"
          class="form-control"
          :class="[(limit !== -1 && !isReadOnly ? paddingTrue ? 'padding-right-60px' : 'padding-right-80px' : ''), (allowClickInputOnTable ? 'allowClickInputOnTable' : '')]"
          size="sm"
          :type="inputType"
          :inputmode="numberInputMode"
          :number="type === 'number'"
          :min="type === 'date' ? '1800-01-01' : min !== null ? min : ''"
          :max="type === 'date' ? '9999-12-31' : max !== null ? max : ''"
          :state="state !== null ? state : type === 'date' && $v.valueInput.$dirty && $v.valueInput.$error ? false : null"
          v-bind="$attrs"
          v-on="mountEventListeners"
          @keypress="handleKeypress"
          @focus="focusField"
          @blur="checkEnteredDate" />
        <span
          v-else
          class="show-data">
          <span v-if="filter === 'formatArea'">{{ valueInput | formatArea }}</span>
          <span v-else-if="filter === 'formatVolume'">{{ valueInput | formatVolume }}</span>
          <span v-else-if="filter === 'formatMeter'">{{ valueInput | formatMeter }}</span>
          <span v-else-if="filter === 'formatDate'">{{ valueInput | formatDate }}</span>
          <span v-else-if="isEllipsize === true">
            {{ truncateText(valueInput) }}
            <coozzy-info-circle-icon
              v-if="valueInput && valueInput.length > 20"
              v-b-tooltip.hover.html="valueInput" />
          </span>
          <span v-else>{{ valueInput !=='' && valueInput !== null ? valueInput : '-' }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon'
function isValidDate(dateString) {
  // force revalidate by using forceValidation variable when user delete whole date
  // (value has empty string in two cases: invalid date and empty date)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const test = this.forceValidation
  if (this.isFocusField) {
    return true
  }
  if (dateString && dateString.length) {
    if (!/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateString)) {
      return false
    }

    const parts = dateString.split('-')
    const year = parseInt(parts[0], 10)

    // Check the ranges of year
    if (year < 1800) {
      return false
    }
  }
  const validityState = this.$refs.bFormInput?.$el?.validity
  if (!validityState) {
    return true
  }
  if (Object.keys(validityState).length === 0) {
    return true
  }
  if (validityState.badInput === true) {
    // invalid date
    return false
  }
  if (validityState.valid === true) {
    // valid date
    return true
  }

  return false
}

export default {
  name: 'CoozzyFormInput',
  components: { CoozzyInfoCircleIcon },
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    paddingTrue: {
      type: Boolean,
      default: true
    },
    state: {
      type: Boolean,
      default: null
    },
    secondaryTitle: {
      type: String,
      default: null
    },
    filter: {
      type: String,
      default: ''
    },
    step: {
      type: Number,
      default: 1
    },
    limit: {
      type: Number,
      default: -1
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => {
        const types = [
          'color', 'date', 'datetime-local', 'email',
          'file', 'hidden', 'image', 'month',
          'number', 'password', 'radio', 'range',
          'reset', 'search', 'submit', 'tel',
          'text', 'time', 'url', 'week'
        ]
        return types.includes(value)
      }
    },
    isEllipsize: {
      type: Boolean,
      default: false
    },
    id: {
      type: [String, Number],
      default: null
    },
    tooltipText: {
      type: String,
      default: null
    },
    min: {
      type: [Number, String],
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    allowClickInputOnTable: {
      type: Boolean,
      default: false
    },
    preventLetters: {
      type: Boolean,
      default: false
    },
    allowNegative: {
      type: Boolean,
      default: false
    },
    useNativeNumberInput: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      turnOnDateValidation: false,
      forceValidation: false,
      isFocusField: false,
      valueInput: this.value
    }
  },
  computed: {
    valueInputLength() {
      return this.normalizeValue(this.valueInput).length
    },
    // Native <input type="number"> does not support selection/caret APIs; use text + inputmode instead.
    inputType() {
      if (this.type !== 'number') {
        return this.type
      }
      return this.useNativeNumberInput ? 'number' : 'text'
    },
    numberInputMode() {
      if (this.type !== 'number') {
        return null
      }
      const step = Number(this.step)
      return step && !Number.isInteger(step) ? 'decimal' : 'numeric'
    },
    mountEventListeners() {
      return Object.assign({},
        this.$listeners,
        {
          input: (value) => {
            if (this.type === 'date') {
              this.$v.$touch()
            }
            const nextValue = this.truncateToLimit(value)
            this.valueInput = nextValue
            this.$emit('input', nextValue)
          },
          paste: ($event) => {
            if ($event.type !== 'paste') {
              return
            }

            const shouldHandlePaste = this.limit !== -1 || this.preventLetters || this.type === 'number'
            if (!shouldHandlePaste) {
              return
            }

            $event.preventDefault()
            const target = $event.target
            const currentText = this.normalizeValue(target?.value ?? this.valueInput)
            const rawPastedText = $event.clipboardData.getData('text')
            const sanitizedPastedText = this.sanitizePastedText(rawPastedText)

            let selectionStart = typeof target?.selectionStart === 'number'
              ? target.selectionStart
              : currentText.length
            let selectionEnd = typeof target?.selectionEnd === 'number'
              ? target.selectionEnd
              : selectionStart

            let nextPastedText = sanitizedPastedText

            if (this.limit !== -1) {
              const remainingSpace = this.limit - (currentText.length - (selectionEnd - selectionStart))
              if (remainingSpace <= 0) {
                return
              }
              nextPastedText = nextPastedText.substring(0, remainingSpace)
            }

            if (!nextPastedText.length && selectionStart === selectionEnd) {
              return
            }

            const newText = currentText.substring(0, selectionStart) +
              nextPastedText +
              currentText.substring(selectionEnd)

            let nextValue = this.truncateToLimit(newText)
            if (this.type === 'number' && nextValue !== '' && nextValue !== null) {
              nextValue = Number(nextValue)
            }
            this.valueInput = nextValue
            this.$emit('input', nextValue)

            const caret = selectionStart + nextPastedText.length
            this.$nextTick(() => {
              const el = this.$refs.bFormInput?.$el || target
              if (el && typeof el.setSelectionRange === 'function') {
                el.setSelectionRange(caret, caret)
              }
            })
          }
        }
      )
    }
  },
  watch: {
    value: function (newVal) {
      this.valueInput = newVal
      if (this.type === 'date') {
        this.forceValidation = !this.forceValidation
        this.$v.$touch()
      }
    }
  },
  mounted() {
    if (this.type === 'date') {
      this.turnOnDateValidation = true
    }
  },
  methods: {
    setFocusInput() {
      this.$nextTick(() => {
        this.$refs.bFormInput.focus()
      })
    },
    handleKeypress(e) {
      this.respectLimit(e)
      if (this.preventLetters || this.type === 'number') {
        this.preventLettersInput(e)
      }
    },
    focusField() {
      this.isFocusField = true
    },
    checkEnteredDate() {
      this.isFocusField = false
      this.forceValidation = !this.forceValidation
      this.$v.$touch()
    },
    truncateText(value) {
      if (value === null || value === '' || value === undefined || value === -1 || value === '-1') {
        return '-'
      } else {
        if (value.length > 20) {
          return value.substring(0, 20) + '...'
        }
        return value
      }
    },
    respectLimit ($event) {
      if (this.type === 'date') {
        this.$v.$touch()
      }
      const currentText = this.normalizeValue(this.valueInput)
      if ((this.limit !== -1 && currentText.length >= this.limit) && window.getSelection().toString() === '') {
        $event?.preventDefault()
      }
    },
    normalizeValue(value) {
      if (value === null || value === undefined) {
        return ''
      }
      return String(value)
    },
    sanitizePastedText(value) {
      const stringValue = this.normalizeValue(value)
      if (this.type === 'number' && this.allowNegative) {
        return this.sanitizeNumberWithOptionalLeadingMinus(stringValue)
      }
      if (this.preventLetters || this.type === 'number') {
        return stringValue.replace(/[^0-9.]/g, '')
      }
      return stringValue
    },
    sanitizeNumberWithOptionalLeadingMinus(value) {
      const normalizedValue = this.normalizeValue(value)
      let sanitized = normalizedValue.replace(/[^0-9.-]/g, '')
      sanitized = sanitized.replace(/(?!^)-/g, '')
      return sanitized
    },
    truncateToLimit(value) {
      if (this.limit === -1) {
        return value
      }
      const stringValue = this.normalizeValue(value)
      if (stringValue.length <= this.limit) {
        return value
      }
      const truncated = stringValue.substring(0, this.limit)
      if (this.type === 'number') {
        return truncated === '' ? null : Number(truncated)
      }
      return truncated
    },
    isInvalid() {
      let valid = true
      this.$v.$touch()
      if (this.$v.$invalid) {
        valid = false
      }
      return !valid
    },
    preventLettersInput(event) {
      if (this.type === 'number' && this.allowNegative && event.key === '-') {
        const target = event?.target
        if (!target) {
          event.preventDefault()
          return
        }
        const currentText = this.normalizeValue(target.value ?? this.valueInput)
        const selectionStart = typeof target.selectionStart === 'number' ? target.selectionStart : 0
        const selectionEnd = typeof target.selectionEnd === 'number' ? target.selectionEnd : selectionStart
        const nextValue = currentText.substring(0, selectionStart) + '-' + currentText.substring(selectionEnd)
        const hasMoreThanOneMinus = (nextValue.match(/-/g) || []).length > 1
        if (selectionStart !== 0 || hasMoreThanOneMinus) {
          event.preventDefault()
        }
        return
      }
      const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
      if (!allowedKeys.includes(event.key)) {
        event.preventDefault()
      }
    }
  },
  validations() {
    const validationInput = {
      valueInput: {
      }
    }
    if (this.turnOnDateValidation) {
      validationInput.valueInput = {
        isValidDate
      }
    }
    return validationInput
  }
}
</script>

<style lang="scss" scoped>
  .root {
    input {
      padding: 0.2em 8px;
    }
  }
  input[type="date"].form-control{
    color: black;
  }
  .show-data{
    padding-left: 0;
  }
  .limit {
    position: absolute;
    right: 2rem;
    top: 0;
    line-height: 30px;
  }
  .padding-right-80px {
    padding-right: 80px !important;
  }
  .padding-right-60px {
    padding-right: 60px !important;
  }
  .secondary-title{
    display: inline;
    font-size: 0.8rem;
  }
</style>
